import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  FormLabel,
} from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { LoadingButton } from "@mui/lab";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { toastError, toastSuccess } from "src/utils/utils";

const textFieldStyles = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#d0d7de",
    },
    "&:hover fieldset": {
      borderColor: "#7338AC",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#7338AC",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#7338AC",
  },
};

const validationSchema = yup.object().shape({
  title: yup.string().required("Title is required"),
  description: yup.string().required("Description is required"),
  file: yup.mixed().when('mode', {
    is: 'add',
    then: (schema) => schema.required("Audio file is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const DialogAddLesson = (props) => {
  const { open, onClose, onSuccess, mode = "add", dataToEdit, moduleId } = props;
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [duration, setDuration] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: "",
      description: "",
      file: null,
    },
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  const watchedFile = watch("file");

  useEffect(() => {
    if (open && dataToEdit && mode === "edit") {
      setFileName(dataToEdit.file ? dataToEdit.file.split('/').pop() : "");
      setDuration(null);
      reset({
        title: dataToEdit.title || "",
        description: dataToEdit.description || "",
        file: null, // Don't pre-populate file for edit
      });
    } else if (open && mode === "add") {
      setFileName("");
      setSelectedFile(null);
      setDuration(null);
      reset({
        title: "",
        description: "",
        file: null,
      });
    }
  }, [dataToEdit, open, mode, reset]);

  // Fetch file when editing
  useEffect(() => {
    if (open && dataToEdit && mode === "edit" && dataToEdit.file) {
      fetchFile(dataToEdit.file);
    }
  }, [open, dataToEdit, mode]);

  const getAudioDuration = (file) => {
    return new Promise((resolve, reject) => {
      const audio = new Audio();
      const url = URL.createObjectURL(file);

      audio.addEventListener('loadedmetadata', () => {
        const durationInSeconds = Math.round(audio.duration);
        URL.revokeObjectURL(url);
        resolve(durationInSeconds);
      });

      audio.addEventListener('error', (error) => {
        URL.revokeObjectURL(url);
        reject(error);
      });

      audio.src = url;
    });
  };

  const fetchFile = async (filePath) => {
    try {
      const response = await fetch(filePath);
      if (response.ok) {
        const blob = await response.blob();
        const fileName = filePath.split('/').pop();
        const file = new File([blob], fileName, { type: 'audio/mpeg' });
        setSelectedFile(file);
        setFileName(file.name);
        setValue("file", file);

        // Get duration for existing file
        try {
          const fileDuration = await getAudioDuration(file);
          setDuration(fileDuration);
        } catch (error) {
          console.error("Error getting duration:", error);
        }
      }
    } catch (error) {
      console.error("Error fetching file:", error);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check if file is MP3 audio
      const isValidAudio = file.type === "audio/mpeg" || file.type === "audio/mp3";

      if (!isValidAudio) {
        toastError("Please select an MP3 audio file");
        return;
      }

      setSelectedFile(file);
      setFileName(file.name);
      setValue("file", file);

      // Get duration of the audio file
      try {
        const fileDuration = await getAudioDuration(file);
        setDuration(fileDuration);
      } catch (error) {
        console.error("Error getting audio duration:", error);
        toastError("Could not read audio file duration");
      }
    }
  };

  const onSubmit = (data) => {
    setLoading(true);

    const formData = new FormData();
    formData.append("title", data.title);
    formData.append("description", data.description);
    formData.append("module_id", moduleId);

    // Append duration if available
    if (duration !== null) {
      formData.append("duration", duration);
    }

    // Only append file if it's a new file (add mode or file changed in edit mode)
    if (mode === "add" && selectedFile) {
      formData.append("file", selectedFile);
    } else if (mode === "edit" && selectedFile) {
      formData.append("file", selectedFile);
    }

    if (mode === "add") {
      axiosInstance
        .post(ApiEndPoints.LESSONS.create, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          toastSuccess(response.data?.message || "Lesson added successfully");
          onSuccess();
          onClose();
        })
        .catch((error) => {
          toastError(error);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // Edit mode - pass id in params, not payload
      const editPayload = {
        title: data.title,
        description: data.description,
        module_id: dataToEdit?.module_id,
      };

      // If file is selected, use FormData, otherwise use JSON
      if (selectedFile) {
        const editFormData = new FormData();
        editFormData.append("title", data.title);
        editFormData.append("description", data.description);
        editFormData.append("module_id", dataToEdit?.module_id);
        editFormData.append("file", selectedFile);

        // Append duration if available
        if (duration !== null) {
          editFormData.append("duration", duration);
        }

        axiosInstance
          .patch(ApiEndPoints.LESSONS.update(dataToEdit.id), editFormData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            toastSuccess(response.data?.message || "Lesson updated successfully");
            onSuccess();
            onClose();
          })
          .catch((error) => {
            toastError(error);
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        // If duration is available and no file is being updated, include it in JSON payload
        const finalEditPayload = { ...editPayload };
        if (duration !== null) {
          finalEditPayload.duration = duration;
        }

        axiosInstance
          .patch(ApiEndPoints.LESSONS.update(dataToEdit.id), finalEditPayload)
          .then((response) => {
            toastSuccess(response.data?.message || "Lesson updated successfully");
            onSuccess();
            onClose();
          })
          .catch((error) => {
            toastError(error);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {mode === "add" ? "Add Lesson" : "Edit Lesson"}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: "grey.500",
            "&:hover": {
              backgroundColor: "grey.100",
            },
          }}
        >
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Box component="form" sx={{ mt: 1 }}>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Title"
                error={!!errors.title}
                helperText={errors.title?.message}
                sx={textFieldStyles}
                margin="normal"
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                multiline
                rows={3}
                label="Description"
                error={!!errors.description}
                helperText={errors.description?.message}
                sx={textFieldStyles}
                margin="normal"
              />
            )}
          />

          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth error={!!errors.file}>
              <FormLabel>Select Audio File</FormLabel>
              <OutlinedInput
                type="file"
                accept=".mp3,audio/mpeg,audio/mp3"
                onChange={handleFileChange}
                startAdornment={
                  <InputAdornment position="start">
                    <CloudUploadIcon />
                  </InputAdornment>
                }
                sx={textFieldStyles}
              />
              {fileName && (
                <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                  {fileName}
                </Typography>
              )}
              {mode === "edit" && !selectedFile && dataToEdit?.file && (
                <Typography variant="caption" sx={{ mt: 1, display: "block", color: "text.secondary" }}>
                  Current file: {dataToEdit.file.split('/').pop()}
                </Typography>
              )}
              {errors.file && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                  {errors.file.message}
                </Typography>
              )}
            </FormControl>
          </Box>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: "#d0d7de",
            color: "#656d76",
            "&:hover": {
              borderColor: "#7338AC",
              color: "#7338AC",
            },
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit(onSubmit)}
          loading={loading}
          variant="contained"
        >
          {mode === "add" ? "Add Lesson" : "Update Lesson"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default DialogAddLesson;
